/**
 * @overview The modal used across the website.
 *
 * @module MegaModal
 */

import $ from 'qwery';
import scrollTop from 'simple-scrolltop';
import { keys } from '../../constants';
import { isKeyEvent, getTarget } from '../../helpers/eventHelper';
import { parseText } from '../../helpers/fetchHelper';
import { killScroll } from '../../helpers/scrollHelper';

export default class MegaModal {

    /**
     * Creates and dispatches a DOM event
     *
     * @param {object} [options={}]
     */
    constructor (options = {}) {

        // set options to our data attributes defined on the megamodal element (if there are any)
        for (const property in options.dataSet) {
            if (options.dataSet.hasOwnProperty(property) && options.dataSet[property] !== null) {
                options[property] = options.dataSet[property];
            }
        }

        /**
         * Define options – can be passed in or set as defaults
         *
         * @type {object}
         * @namespace
         */
        this.options = {
            /**
             * The element from which the modal was triggered
             *
             * @type {HTMLElement}
             */
            opener: options.opener || document.activeElement,

            /**
             * Describes the type of modal we want to display
             *
             * @type {string}
             */
            modalType: (['fullscreen', 'fullscreen--touch', 'full', 'narrow', 'receipt'].indexOf(options.modalType) > -1 ? options.modalType : false) || 'full',

            /**
             * The title to be displayed on the modal box
             *
             * @type {string|undefined}
             */
            title: options.title || null,

            /**
             * The content to be displayed inside the modal box
             *
             * Possible Values:
             * element – content then to be specified with selector of content to put into modal
             * text – content will then simply be a text string
             *
             * @type {string}
             */
            content: options.content || 'Are you sure you want to do this?',

            /**
             * The buttons to be displayed inside the modal box
             *
             * @type {object[]}
             */
            buttons: (function () {

                if (options.buttons === 'default') {

                    return [
                        {
                            text: 'Cancel',
                            event: 'cancel',
                            className: 'btn btn--dour'
                        },
                        {
                            text: 'Confirm',
                            event: 'confirm',
                            className: 'btn'
                        }
                    ];

                }
                if (options.buttons) {

                    return JSON.parse(options.buttons);

                }
                return []; //else return null
            }()),

            /**
             * Whether clicking outside the modal closes it
             *
             * @type {boolean}
             */
            clickOutsideToClose: (options.clickOutsideToClose !== 'false'
                                    && options.clickOutsideToClose !== false),

            /**
             * What event gets fired when clicked outside
             *
             * @type {string}
             */
            clickOutsideEvent: options.clickOutsideEvent || 'cancel',

            /**
             * Whether the modal has a close icon in the top right
             *
             * @type {boolean}
             */
            closeBtn: (options.closeBtn !== 'false'
                        && options.closeBtn !== false),

            /**
             * Classname to be applied to the modal
             *
             * @type {string}
             */
            className: options.className ? options.className.toString() : '',


            /**
             * Whether or not to save the scroll position of the modal when opened, to return to that position when closed
             *
             * Mainly useful for when needing to apply the .mm-modal-overlay--unFix class to fix
             * iOS fixed position issues with interactive elements inside modals
             *
             * @type {boolean}
             */
            saveScrollPosition: (options.saveScrollPosition !== 'false'
                                    && options.saveScrollPosition !== false),

            /**
             * If there is a delay to hiding the modal (useful for CSS transitions)
             *
             * @type {string}
             */
            hideDelay: /^\d*\.?\d+$/.test(options.hideDelay) ? options.hideDelay : 300,

            /**
             * Target URL
             *
             * @type {string}
             */
            targetURL: (function () {

                if (options.targetURL) return options.targetURL;

                try {
                    if (window.event.target.href) {
                        return window.event.target.href;
                    }
                } catch (e) {
                    return null;
                }

                return null;
            }()),

            /**
             * Callback for once the modal has been added to the DOM
             *
             * @type {function}
             */
            onShow: options.onShow || null,

            beforeShow: options.beforeShow || null,

            afterShow: options.afterShow || null,

            onHide: options.onHide || null,

            /**
             * Callback fires before the modal has been deleted from the DOM
             *
             * @type {function}
             */
            beforeHide: options.beforeHide || this.beforeHide
        };

        this.options.pageClass = (this.options.modalType.includes('fullscreen')) ? 'is-modalFullScreen' : 'has-openModal';
        this.options.scrollPosition = 0;

        // Bind the context of our object to our methods, so we don’t get weird instances of this
        const methods = ['constructModal', 'show', 'hide', 'constructBtn', 'modalEvent', 'onShow', 'destroyModal'];
        const context = this;

        for (let i = 0, l = methods.length; i < l; i++) {
            context[methods[i]] = context[methods[i]].bind(context);
        }

        this.modal = {
            title: null,
            content: null,
            controls: null,
            el: null
        };

        this.modal.el = this.constructModal();

    }

    /**
     * Constructs the modal element
     *
     * @returns {HTMLElement} - Reference to created modal element
     */
    constructModal () {

        //create the modal html and attach to the dom
        //
        //.modal-overlay
        //  .modal-content
        //  h2.modal-title
        //  p.modal-text
        //  .modal-controls
        //      button.btn

        // Initate vars
        var modalWrapper = document.createElement('div'),
            modal = document.createElement('div');

        //set modal wrapper and modal element properties
        modalWrapper.id = 'mm-modal';
        modalWrapper.classList.add('mm-modal-overlay');
        modalWrapper.setAttribute('role', 'dialog');

        modal.classList.add('mm-modal');
        modal.classList.add(`mm-modal--${this.options.modalType}`);

        modal.setAttribute('data-test-id', 'megamodal');

        // if we have any custom classnames, add them to our wrapper
        if (this.options.className !== '') {
            modalWrapper = this.appendCustomClasses(modalWrapper);
        }

        //create title element
        if (this.setTitle()) {
            modalWrapper.setAttribute('aria-label', this.options.title);
        }

        //create content element
        this.modal.content = document.createElement('div');
        this.modal.content.classList.add('mm-modal-content');
        this.applyContent();

        //create modal controls
        if (this.options.buttons.length > 0) {
            this.modal.controls = document.createElement('div');
            this.modal.controls.classList.add('mm-modal-controls');

            //fill the controls with our buttons
            for (let i = 0, btnLength = this.options.buttons.length; i < btnLength; i++) {
                this.modal.controls.appendChild(this.constructBtn(this.options.buttons[i]));
            }
        }

        //now append all the different parts together
        if (this.modal.title !== null) {
            modal.appendChild(this.modal.title);
        }

        modal.appendChild(this.modal.content);

        // append controls if they exist
        if (this.modal.controls !== null) {
            modal.appendChild(this.modal.controls);
        }

        const closeBtn = this.createCloseBtn();

        if (closeBtn !== false) {
            modal.appendChild(closeBtn);
        }

        modalWrapper.appendChild(modal);

        if (this.options.clickOutsideToClose) {

            modalWrapper.addEventListener('click', event => {
                if (event.target === modalWrapper) {
                    this.hide(event);
                }
            });

        }

        return modalWrapper;

    }

    /**
     * Appends a comma separated string of classes to an element, returning the element with classes appended
     *
     * @param  {HTMLElement} el - Element to append Classes to
     * @param  {string} customClasses - Comma Separated list of classnames
     *
     * @returns {HTMLElement} - Return element with classes appended to it
     */
    appendCustomClasses (el) {

        var classNames = this.options.className.split(',');

        if (classNames.length > 1) {

            for (let i = 0, numClasses = classNames.length; i < numClasses; i++) {
                el.classList.add(classNames[i]);
            }

        } else {
            el.classList.add(this.options.className);
        }

        return el;

    }

    /**
     * Creates Close Btn for the Modal, if set to do so in our modal options
     *
     * @returns {HTMLElement|false} - Returns created button or false if no button is created
     */
    createCloseBtn () {

        if (this.options.closeBtn) {

            const closeBtn = document.createElement('button');
            closeBtn.classList.add('mm-modal-close', 'btn', 'btn--icon', 'icon', 'icon--close');
            closeBtn.innerHTML = 'Close';

            closeBtn.addEventListener('click', this.hide);

            return closeBtn;

        }

        return false;

    }

    /**
     * Set the title of the modal
     *
     * @param {string} title
     *
     * @returns true if a title was set, otherwise false
     */
    setTitle (title) {

        const modalTitle = (title === null ? title : this.options.title);

        //if we still have a title, create element and set it, else do nothing (title will be null)
        if (modalTitle !== null) {
            this.modal.title = document.createElement('h2');
            this.modal.title.classList.add('mm-modal-title');
            this.modal.title.innerHTML = modalTitle;

            return true;
        }

        return false;

    }

    /**
     * Applys the content in our options to our modal
     * Checks if the content is a selector referencing a HTML element selector – if it is, it sets that element as the content
     * otherwise it just sets it to the calue in options.content
     */
    applyContent () {

        if ((this.options.content.charAt(0) === '.') || (this.options.content.charAt(0) === '#') || (this.options.content.charAt(0) === '[')) {

            const contentEl = document.querySelector(this.options.content);
            this.modal.content.innerHTML = contentEl.innerHTML;


        // if the content starts with a http, it’s a URL so get it via AJAX and then insert it
        } else if (this.options.content.substring(0, 4) === 'url:') {

            const contentUrl = this.options.content.substring(4);

            this.modal.content.classList.add('is-loading', 'is-loading--small');

            fetch(contentUrl, { credentials: 'same-origin' })
                .then(parseText)
                .then(data => {
                    this.modal.content.innerHTML = data;
                    this.modal.content.classList.remove('is-loading', 'is-loading--small');
                });

        } else {
            this.modal.content.innerHTML = this.options.content;
        }

    }

    /**
     * Destroys the modal if it exists
     */
    destroyModal () {

        var modal = document.getElementById('mm-modal');

        if (this.options.opener) {
            this.options.opener.focus(); // shift the focus back to the element that opened the modal
        }

        if (modal) {
            modal.parentElement.removeChild(modal);
        }

    }

    bindEvents () {

        const focusableEls = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), '
                            + 'button:not([disabled]), iframe, object, embed, *[tabindex], *[contenteditable]';

        // Filter any "hidden" elements
        this.focusableItems = $(focusableEls, this.modal.el).filter(el => el.offsetHeight > 0);

        // close the modal when the escape key is hit
        document.addEventListener('keydown', event => {
            if (isKeyEvent(event, keys.ESCAPE)) {
                this.hide(event);
            }

            if (isKeyEvent(event, keys.TAB) && this.focusableItems.length > 0) {
                const numberOfFocusableItems = this.focusableItems.length;
                const focusedItem = document.activeElement;
                const focusedItemIndex = this.focusableItems.indexOf(focusedItem);

                if (event.shiftKey) {
                    if (focusedItemIndex === 0) {
                        // if focused on first item and user preses back-tab, go to the last focusable item
                        this.focusableItems[numberOfFocusableItems - 1].focus();
                        event.preventDefault();
                    }
                } else {
                    /* eslint-disable no-lonely-if */
                    if (focusedItemIndex === numberOfFocusableItems - 1) {
                        // if focused on the last item and user preses tab, go to the first focusable item
                        this.focusableItems[0].focus();
                        event.preventDefault();
                    }
                    /* eslint-enable no-lonely-if */
                }
            }
        });

        document.addEventListener('MegaModal:close', event => {
            this.hide(event);
        });

    }

    /**
     * Show the modal – with hooks for beforeShow and onShow
     */
    show (event) {

        if (event) {
            event.preventDefault(); //stop link going anywhere if initialised with an event
        }

        setTimeout(() => {
            if (this.focusableItems.length > 0) {
                this.focusableItems[0].focus();
            }
        }, 0);

        if (typeof this.options.beforeShow === 'function') {
            this.options.beforeShow();
        }

        //TODO– check if a modal is already showing (in the DOM)
        document.body.appendChild(this.modal.el);

        this.bindEvents();

        this.onShow.call(this);

        if (typeof this.options.onShow === 'function') {
            this.options.onShow.call(this);
        }

    }

    /**
     * Hide Modal – with hooks for beforeHide
     */
    hide (event) {

        if (typeof this.options.beforeHide === 'function') {
            this.options.beforeHide.call(this, this.destroyModal);
        } else {
            //destroy the modal window
            this.destroyModal();
        }

        if (typeof this.options.onHide === 'function') {
            this.options.onHide.call(this, event);
        }

    }

    /**
     * Constructs button controls for the modal
     *
     * @param {object} btnOptions - Settings for the button to be constructed
     *
     * @returns {HTMLElement} - Constructed Button Element
     */
    constructBtn (btnOptions) {

        const btn = document.createElement('button');

        if (btnOptions.className) {
            btn.className = btnOptions.className;
        }

        btn.innerHTML = btnOptions.text;

        if (btnOptions.event) {
            const _self = this;

            btn.addEventListener('click', () => {
                _self.modalEvent(btnOptions.event);
            });
        }

        return btn;

    }

    /**
     * Fire modal event when control button is clicked
     * @param {String} event - type of event being fired
     */
    modalEvent (event) {

        //at the moment, all events hide the modal – could change this later if needed
        this.hide(event);

        // if confirmation, use the link from our initial event
        if (event === 'confirm' && this.options.targetURL !== null) {
            window.location.href = this.options.targetURL;
        }
    }

    /**
     * Adds show class to the modal to show the modal in the CSS
     * Fires afterShow if one exists
     */
    onShow () {
        // this.modal.el.offsetWidth = this.modal.el.offsetWidth;
        this.options.scrollPosition = scrollTop(); // save scroll position
        this.modal.el.classList.add('show');
        $('html')[0].classList.toggle(this.options.pageClass);

        // if saving scroll position AND if modal type is fullscreen narrow and under certain width
        if (this.options.saveScrollPosition
            && ((this.options.modalType === 'fullscreen--touch' && window.matchMedia('(hover: none)').matches) || this.options.modalType === 'fullscreen')) {
            scrollTop(0);
        }

        // if fullscreen, kill scrolling so background doesn't wobble around
        if (this.options.modalType.includes('fullscreen')) {
            killScroll('[class*=mm-modal--fullscreen]');
        }

        if (typeof this.options.afterShow === 'function') {
            this.options.afterShow();
        }
    }

    /**
     * By default - beforeHide removes the show class to hide the modal and waits for the hideDelay before calling the callback
     *
     * @param  {Function} cb - callback to fire once function has completed
     */
    beforeHide (cb) {

        //we delete the show class on the modal wrapper, wait x ms (as defined by the user – by default this is set to 0, so it fires immmediately)
        //and then destroy the modal
        $('html')[0].classList.remove(this.options.pageClass);

        this.modal.el.classList.remove('show');

        if (this.options.saveScrollPosition) {
            scrollTop(this.options.scrollPosition);
        }

        setTimeout(cb, this.options.hideDelay);
    }

}

/**
 * Initialises all MegaModal on a page if data attr 'data-megamodal' is present on elements in the DOM
 * This will self execute when the module is first loaded
 */
MegaModal.init = function () {

    function modalActionFired (event) {
        event.preventDefault();

        //look for any set vars on the elements
        const dataSet = {
            modalType: this.getAttribute('data-megamodal-modaltype'),
            title: this.getAttribute('data-megamodal-title'),
            className: this.getAttribute('data-megamodal-classname'),
            content: this.getAttribute('data-megamodal-content'),
            buttons: this.getAttribute('data-megamodal-buttons'),
            clickOutsideToClose: this.getAttribute('data-megamodal-clickoutsidetoclose'),
            clickOutsideEvent: this.getAttribute('data-megamodal-clickoutsideevent'),
            closeBtn: this.getAttribute('data-megamodal-closebtn'),
            hideDelay: this.getAttribute('data-megamodal-hidedelay'),
            targetURL: this.getAttribute('data-megamodal-targeturl')
        };
        const modal = new MegaModal({ dataSet });

        modal.show();
    }

    //Attach events to mega modal click elements with the megamodal attribute
    const MMElements = document.querySelectorAll('[data-megamodal]');
    for (let i = 0; i < MMElements.length; i++) {
        MMElements[i].addEventListener('click', modalActionFired);
    }

    //This function wraps 'modalActionFired' so that the dynamic elements
    //can have a different context(this) but still use the same modal show function.
    function modalDynamicAttacherActionFired (e) {
        const elem = getTarget(e).closest('[data-megamodal-dynamic]');
        if (elem) {
            modalActionFired.call(elem, e);
        }
    }

    //Attach events to mega modal dynamic attacher
    //this watches for click events further down the DOM originating from within
    //elements with the megamodal-dynamic attribute
    const MMDAElements = document.querySelectorAll('[data-megamodal-dynamic-attacher]');
    for (let i = 0; i < MMDAElements.length; i++) {
        MMDAElements[i].addEventListener('click', modalDynamicAttacherActionFired);
    }
};

MegaModal.init();

/**
 * Factory method for creating a MegaModal object
 *
 * @param {object} options - The options to override the defaults
 */
MegaModal.attach = function (options) {
    return new MegaModal(options);
};

window.MegaModal = MegaModal; // this can die when the old JS has been moved over to modules – only there to expose the functions to non-modules
